import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertCircle, ExternalLink, Loader2, Shield, CheckCircle2, Copy, Settings2 } from "lucide-react";
import { Instagram, Twitter, Linkedin, Facebook, AtSign, Youtube } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { cn } from "@/lib/utils";

interface ConnectAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnected: () => void;
}

// SVG icons for platforms not in lucide-react
const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.22 8.22 0 004.8 1.54V6.78a4.85 4.85 0 01-1.03-.09z"/>
  </svg>
);

const PinterestIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

const ALL_PLATFORMS = [
  {
    value: 'twitter',
    label: 'X (Twitter)',
    icon: Twitter,
    color: 'bg-black',
    description: 'Publish tweets, threads, and view engagement analytics.',
    note: null,
    defaultEnabled: true,
  },
  {
    value: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    color: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
    description: 'Schedule posts, Stories, and Reels. Business/Creator accounts only.',
    note: 'Requires a Facebook Page linked to an Instagram Professional account.',
    defaultEnabled: true,
  },
  {
    value: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    color: 'bg-[#1877F2]',
    description: 'Publish to Facebook Pages and view page insights.',
    note: 'You must be an admin of at least one Facebook Page.',
    defaultEnabled: true,
  },
  {
    value: 'linkedin',
    label: 'LinkedIn',
    icon: Linkedin,
    color: 'bg-[#0A66C2]',
    description: 'Share professional updates and articles on your LinkedIn profile.',
    note: null,
    defaultEnabled: true,
  },
  {
    value: 'threads',
    label: 'Threads',
    icon: AtSign,
    color: 'bg-black',
    description: 'Publish posts and manage replies on Threads.',
    note: 'Your Threads app must be submitted for review or you must be added as a test user.',
    defaultEnabled: true,
  },
  {
    value: 'youtube',
    label: 'YouTube',
    icon: Youtube,
    color: 'bg-[#FF0000]',
    description: 'Upload videos and Shorts to your YouTube channel.',
    note: null,
    defaultEnabled: false,
  },
  {
    value: 'tiktok',
    label: 'TikTok',
    icon: TikTokIcon,
    color: 'bg-black',
    description: 'Publish videos to TikTok using the Content Posting API.',
    note: 'TikTok only supports video posts — text-only content is not supported.',
    defaultEnabled: false,
  },
  {
    value: 'pinterest',
    label: 'Pinterest',
    icon: PinterestIcon,
    color: 'bg-[#E60023]',
    description: 'Create Pins and publish to your Pinterest boards.',
    note: null,
    defaultEnabled: false,
  },
];

interface LinkedInPage { urn: string; id: number; name: string; vanityName: string; accessToken: string; }

export const ConnectAccountDialog = ({ open, onOpenChange, onConnected }: ConnectAccountDialogProps) => {
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [popupBlocked, setPopupBlocked] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [platformEnabled, setPlatformEnabled] = useState<Record<string, boolean>>({});
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [linkedinPages, setLinkedinPages] = useState<LinkedInPage[]>([]);
  const [connectingPage, setConnectingPage] = useState(false);
  const { toast } = useToast();
  const { orgId } = useOrgPermissions();

  const callbackUrl = `${window.location.origin}/oauth/callback`;

  // Load saved preferences
  useEffect(() => {
    if (!open) return;
    const load = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await (supabase as any)
        .from('platform_preferences')
        .select('platform, is_enabled')
        .eq('user_id', user.id);

      const saved: Record<string, boolean> = {};
      for (const row of data ?? []) saved[row.platform] = row.is_enabled;

      // Merge with defaults
      const merged: Record<string, boolean> = {};
      for (const p of ALL_PLATFORMS) {
        merged[p.value] = saved[p.value] !== undefined ? saved[p.value] : p.defaultEnabled;
      }
      setPlatformEnabled(merged);
    };
    load();
  }, [open]);

  const visiblePlatforms = ALL_PLATFORMS.filter(p => platformEnabled[p.value] !== false);

  const copyCallbackUrl = () => {
    navigator.clipboard.writeText(callbackUrl).then(() => {
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    });
  };

  const togglePlatform = async (platform: string, enabled: boolean) => {
    setPlatformEnabled(prev => ({ ...prev, [platform]: enabled }));
    setSavingPrefs(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      await (supabase as any)
        .from('platform_preferences')
        .upsert({ user_id: user.id, platform, is_enabled: enabled }, { onConflict: 'user_id,platform' });
    } finally {
      setSavingPrefs(false);
    }
    if (!enabled && selectedPlatform === platform) setSelectedPlatform(null);
  };

  const selected = ALL_PLATFORMS.find(p => p.value === selectedPlatform);

  const handleConnect = async () => {
    if (!selectedPlatform) return;
    setLoading(true);
    setPopupBlocked(false);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const redirectUri = `${window.location.origin}/oauth/callback`;
      const { data: { session } } = await supabase.auth.getSession();
      const { data: initData, error: initError } = await supabase.functions.invoke(
        `${selectedPlatform}-oauth-init`,
        {
          body: { redirectUrl: window.location.origin },
          headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
        }
      );

      if (initError) {
        let msg = initError.message ?? 'Failed to start OAuth';
        try {
          const body = await (initError as any).context?.json?.();
          if (body?.error) msg = body.error;
        } catch {}
        throw new Error(msg);
      }
      if (!initData?.authUrl) throw new Error('No authorization URL returned');

      ['oauth_state','oauth_platform','oauth_code_verifier','oauth_user_id','oauth_redirect_uri','oauth_org_id']
        .forEach(k => sessionStorage.removeItem(k));

      localStorage.setItem('oauth_platform', selectedPlatform);
      localStorage.setItem('oauth_state', initData.state);
      localStorage.setItem('oauth_redirect_uri', redirectUri);
      localStorage.setItem('oauth_ts', String(Date.now()));
      localStorage.setItem('oauth_user_id', user.id);
      if (initData.codeVerifier) localStorage.setItem('oauth_code_verifier', initData.codeVerifier);
      else localStorage.removeItem('oauth_code_verifier');
      if (orgId) localStorage.setItem('oauth_org_id', orgId);
      else localStorage.removeItem('oauth_org_id');

      const w = 600, h = 700;
      const left = Math.round(window.screen.width / 2 - w / 2);
      const top = Math.round(window.screen.height / 2 - h / 2);
      const popup = window.open(
        initData.authUrl,
        'conorbit-oauth',
        `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
      );

      if (!popup || popup.closed || typeof popup.closed === 'undefined') {
        setPopupBlocked(true);
        setLoading(false);
        return;
      }

      const handleMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;
        if (event.data?.type === 'oauth-success') {
          window.removeEventListener('message', handleMessage);
          clearInterval(closedCheck);
          onConnected();
          setLoading(false);
          // LinkedIn: if pages returned, show page picker before closing
          const pages: LinkedInPage[] = event.data?.data?.linkedinPages ?? [];
          if (selectedPlatform === 'linkedin' && pages.length > 0) {
            setLinkedinPages(pages);
            toast({ title: 'LinkedIn profile connected', description: 'You can also connect a LinkedIn Page below.' });
          } else {
            toast({ title: 'Account connected', description: `Your ${selected?.label ?? selectedPlatform} account was connected successfully.` });
            onOpenChange(false);
            setSelectedPlatform(null);
          }
        } else if (event.data?.type === 'oauth-error') {
          window.removeEventListener('message', handleMessage);
          clearInterval(closedCheck);
          toast({ title: 'Connection failed', description: event.data.error ?? 'Failed to connect account.', variant: 'destructive' });
          setLoading(false);
        }
      };

      window.addEventListener('message', handleMessage);
      const closedCheck = setInterval(() => {
        if (popup.closed) {
          clearInterval(closedCheck);
          window.removeEventListener('message', handleMessage);
          setLoading(false);
        }
      }, 500);

    } catch (error: any) {
      toast({ title: 'Connection failed', description: error.message ?? 'Failed to start authorization.', variant: 'destructive' });
      setLoading(false);
    }
  };

  const handleConnectPage = async (page: LinkedInPage) => {
    setConnectingPage(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('linkedin-connect-page', {
        body: { userId: user.id, orgUrn: page.urn, orgName: page.name, vanityName: page.vanityName, accessToken: page.accessToken, ...(orgId ? { orgId } : {}) },
        headers: session ? { Authorization: `Bearer ${session.access_token}` } : {},
      });
      if (error || data?.error) throw new Error(error?.message ?? data?.error ?? 'Failed');
      onConnected();
      toast({ title: 'LinkedIn Page connected', description: `"${page.name}" is now connected.` });
      setLinkedinPages([]);
      onOpenChange(false);
      setSelectedPlatform(null);
    } catch (e: any) {
      toast({ title: 'Failed to connect page', description: e.message, variant: 'destructive' });
    } finally {
      setConnectingPage(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!loading) {
      setSelectedPlatform(null);
      setPopupBlocked(false);
      setLinkedinPages([]);
      onOpenChange(open);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Connect Social Account</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="connect">
          <TabsList className="w-full mb-4">
            <TabsTrigger value="connect" className="flex-1">Connect Account</TabsTrigger>
            <TabsTrigger value="manage" className="flex-1">
              <Settings2 className="w-3.5 h-3.5 mr-1.5" />
              Manage Platforms
            </TabsTrigger>
          </TabsList>

          {/* ── Connect tab ── */}
          <TabsContent value="connect" className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              {visiblePlatforms.map((p) => {
                const Icon = p.icon;
                const isSelected = selectedPlatform === p.value;
                return (
                  <button
                    key={p.value}
                    onClick={() => { setSelectedPlatform(p.value); setPopupBlocked(false); }}
                    className={cn(
                      'flex items-center gap-3 p-3 rounded-xl border-2 text-left transition-all',
                      isSelected ? 'border-[#006B3C] bg-[#006B3C]/5' : 'border-border hover:border-muted-foreground/40'
                    )}
                  >
                    <div className={`w-8 h-8 rounded-lg ${p.color} flex items-center justify-center text-white shrink-0`}>
                      <Icon />
                    </div>
                    <p className="text-[13px] font-semibold">{p.label}</p>
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-[#006B3C] ml-auto shrink-0" />}
                  </button>
                );
              })}
            </div>

            {selected && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">{selected.description}</p>

                {selected.note && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <p className="text-xs leading-relaxed">{selected.note}</p>
                  </div>
                )}

                <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
                  <Shield className="h-4 w-4 text-[#006B3C] shrink-0" />
                  <p className="text-xs text-muted-foreground">
                    Your credentials are encrypted and we never store your password. Access can be revoked any time.
                  </p>
                </div>

                <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-1.5">
                  <p className="text-[11px] font-medium text-muted-foreground">
                    Add this as an authorised redirect URI in your {selected.label} developer portal:
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-[11px] bg-background border border-border rounded px-2 py-1 break-all">
                      {callbackUrl}
                    </code>
                    <Button size="sm" variant="outline" className="h-7 shrink-0 text-xs" onClick={copyCallbackUrl}>
                      <Copy className="w-3 h-3 mr-1" />
                      {urlCopied ? 'Copied!' : 'Copy'}
                    </Button>
                  </div>
                </div>

                {popupBlocked && (
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <p className="font-medium">Popup blocked.</p>
                      <p className="mt-0.5">Allow popups for this site in your browser settings, then try again.</p>
                    </div>
                  </div>
                )}

                <Button onClick={handleConnect} disabled={loading} className="w-full bg-[#006B3C] hover:bg-[#005a32] text-white">
                  {loading
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Awaiting authorization…</>
                    : <><ExternalLink className="w-4 h-4 mr-2" /> Authorize {selected.label}</>
                  }
                </Button>

                <p className="text-[11px] text-center text-muted-foreground">
                  A secure popup will open to complete authorization on {selected.label}'s website.
                </p>
              </div>
            )}

            {/* LinkedIn page picker — shown after profile is connected */}
            {linkedinPages.length > 0 && (
              <div className="space-y-3 border-t border-border pt-4 mt-2">
                <div>
                  <p className="text-sm font-semibold">Connect a LinkedIn Page</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    You admin the following pages. Connect one to post as your organisation.
                  </p>
                </div>
                {linkedinPages.map(page => (
                  <div key={page.urn} className="flex items-center justify-between p-3 rounded-xl border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-[#0A66C2] flex items-center justify-center text-white shrink-0">
                        <Linkedin className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium leading-tight">{page.name}</p>
                        {page.vanityName && <p className="text-xs text-muted-foreground">linkedin.com/company/{page.vanityName}</p>}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleConnectPage(page)} disabled={connectingPage}>
                      {connectingPage ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect'}
                    </Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="w-full text-muted-foreground" onClick={() => { setLinkedinPages([]); onOpenChange(false); setSelectedPlatform(null); }}>
                  Skip — profile only
                </Button>
              </div>
            )}
          </TabsContent>

          {/* ── Manage platforms tab ── */}
          <TabsContent value="manage" className="space-y-2">
            <p className="text-sm text-muted-foreground mb-3">
              Enable or disable platforms to control which ones appear in your Connect dialog and composer.
            </p>
            {ALL_PLATFORMS.map((p) => {
              const Icon = p.icon;
              const enabled = platformEnabled[p.value] !== false;
              return (
                <div key={p.value} className="flex items-center justify-between p-3 rounded-xl border border-border">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${p.color} flex items-center justify-center text-white shrink-0`}>
                      <Icon />
                    </div>
                    <div>
                      <p className="text-sm font-medium">{p.label}</p>
                      <p className="text-xs text-muted-foreground">{p.description.slice(0, 55)}…</p>
                    </div>
                  </div>
                  <Switch
                    checked={enabled}
                    onCheckedChange={(v) => togglePlatform(p.value, v)}
                    disabled={savingPrefs}
                  />
                </div>
              );
            })}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
